/**
 * Boreless Nation — Landing de prelaunch V2 (OFF/ON + cuenta atrás).
 *
 * - Progressive enhancement: sin JS se ve el estado ON con el formulario.
 *   Con JS y en fase prelaunch aparece el gate OFF hasta activarlo.
 * - Transición temporal robusta 08:45 -> 09:00 -> 14:00: la fase se
 *   re-evalua en cada tick, así el cambio a "cutoff" a las 08:45 ocurre solo
 *   aunque la cuenta atrás apunte a las 09:00.
 * - Un solo setInterval por instancia, limpiado en shopify:section:unload.
 * - Fechas ISO con offset (no se interpreta la zona local).
 * - sessionStorage solo para recordar ON en la pestaña actual; si no está
 *   disponible, la experiencia sigue funcionando.
 * - No cookies, sin localStorage, sin polling de red.
 */
(() => {
  const PHASES = ['prelaunch', 'cutoff', 'early', 'public'];
  const pad = (n) => String(n).padStart(2, '0');
  const SS_KEY = 'bn_prelaunch_on';

  const phaseFromTime = (now, cutoff, early, pub) => {
    if (now >= pub) return 'public';
    if (now >= early) return 'early';
    if (now >= cutoff) return 'cutoff';
    return 'prelaunch';
  };

  const ssGet = (k) => {
    try { return window.sessionStorage.getItem(k); } catch (e) { return null; }
  };
  const ssSet = (k, v) => {
    try { window.sessionStorage.setItem(k, v); } catch (e) { /* no-op */ }
  };

  const init = (root) => {
    if (!root || root.dataset.bnInit === 'true') return;
    root.dataset.bnInit = 'true';

    const cutoff = new Date(root.dataset.bnCutoff);
    const early = new Date(root.dataset.bnEarly);
    const pub = new Date(root.dataset.bnPublic);
    if ([cutoff, early, pub].some((d) => Number.isNaN(d.getTime()))) return;

    const designMode = root.dataset.bnDesignMode === 'true';
    const offonEnabled = root.dataset.bnOffon === 'true';
    let override = null;
    if (designMode) {
      const p = new URLSearchParams(window.location.search).get('bn_time_test');
      if (PHASES.includes(p)) override = p;
    }

    const els = {
      phases: root.querySelectorAll('[data-bn-phase]'),
      off: root.querySelector('[data-bn-off]'),
      on: root.querySelector('[data-bn-on]'),
      switchBtn: root.querySelector('[data-bn-switch]'),
      switchState: root.querySelector('[data-bn-switch-state]'),
      countdown: root.querySelector('[data-bn-countdown]'),
      label: root.querySelector('[data-bn-countdown-label]'),
      a11y: root.querySelector('[data-bn-countdown-a11y]'),
      d: root.querySelector('[data-bn-d]'),
      h: root.querySelector('[data-bn-h]'),
      m: root.querySelector('[data-bn-m]'),
      s: root.querySelector('[data-bn-s]'),
      submit: root.querySelector('[data-bn-submit]'),
    };

    const controller = new AbortController();
    let timer = null;
    let currentPhase = null;
    let currentTarget = null;
    // El formulario ya se envió (éxito o error): nunca mostrar el gate OFF.
    const hasFormResult = !!root.querySelector('[data-bn-form-result]')
      || /[?&]customer_posted=true/.test(window.location.search);
    // Recordado ON en esta pestaña, o el usuario acaba de activarlo.
    let activated = ssGet(SS_KEY) === '1' || hasFormResult;

    const stopTimer = () => {
      if (timer) { clearInterval(timer); timer = null; }
    };

    const fmtMadrid = (date) => {
      try {
        return new Intl.DateTimeFormat('es-ES', {
          timeZone: 'Europe/Madrid', dateStyle: 'long', timeStyle: 'short',
        }).format(date);
      } catch (e) { return date.toISOString(); }
    };

    const renderClock = () => {
      if (!currentTarget) return;
      let rem = currentTarget.getTime() - Date.now();
      if (rem < 0) rem = 0;
      const secs = Math.floor(rem / 1000);
      if (els.d) els.d.textContent = pad(Math.floor(secs / 86400));
      if (els.h) els.h.textContent = pad(Math.floor((secs % 86400) / 3600));
      if (els.m) els.m.textContent = pad(Math.floor((secs % 3600) / 60));
      if (els.s) els.s.textContent = pad(secs % 60);
    };

    // El gate OFF solo aplica en prelaunch, con JS, si esta activado y el
    // usuario no lo ha desbloqueado (ni hay resultado de formulario).
    const applyGate = (phase) => {
      const gateActive = offonEnabled && phase === 'prelaunch' && !activated;
      root.classList.toggle('bn-pl--gate-active', gateActive);
      if (els.off) els.off.hidden = !gateActive;
      if (els.switchBtn) els.switchBtn.setAttribute('aria-pressed', String(!gateActive));
      if (els.switchState) els.switchState.textContent = gateActive ? 'OFF' : 'ON';
    };

    const showPhaseBlock = (phase) => {
      els.phases.forEach((el) => { el.hidden = el.getAttribute('data-bn-phase') !== phase; });
    };

    const setCountdown = (phase) => {
      let target = null;
      let label = '';
      if (phase === 'prelaunch' || phase === 'cutoff') { target = early; label = 'Acceso anticipado en'; }
      else if (phase === 'early') { target = pub; label = 'Apertura pública en'; }

      currentTarget = (target && target.getTime() - Date.now() > 0) ? target : null;
      if (currentTarget) {
        if (els.countdown) els.countdown.hidden = false;
        if (els.label) els.label.textContent = label;
        if (els.a11y) els.a11y.textContent = label + ' ' + fmtMadrid(currentTarget) + ' (hora peninsular de España).';
        renderClock();
      } else if (els.countdown) {
        els.countdown.hidden = true;
      }
    };

    const render = () => {
      currentPhase = override || phaseFromTime(Date.now(), cutoff.getTime(), early.getTime(), pub.getTime());
      showPhaseBlock(currentPhase);
      applyGate(currentPhase);
      setCountdown(currentPhase);
    };

    // Un unico intervalo: cada segundo re-evalua la fase (transiciones
    // 08:45/09:00/14:00) y, si no cambia, actualiza el reloj.
    const tick = () => {
      const phase = override || phaseFromTime(Date.now(), cutoff.getTime(), early.getTime(), pub.getTime());
      if (phase !== currentPhase) { render(); return; }
      renderClock();
    };

    // Activar ON desde el gate.
    const activateOn = () => {
      activated = true;
      ssSet(SS_KEY, '1');
      applyGate(currentPhase);
      // Fundido corto del bloque ON (respeta prefers-reduced-motion via CSS).
      if (els.on) {
        els.on.style.opacity = '0';
        requestAnimationFrame(() => { els.on.style.opacity = '1'; });
      }
      // Mover el foco de forma accesible al primer control del contenido.
      const focusTarget = root.querySelector('[data-bn-email-field]')
        || root.querySelector('[data-bn-on] h1')
        || els.on;
      if (focusTarget) {
        if (!focusTarget.hasAttribute('tabindex') && focusTarget.tagName !== 'INPUT') {
          focusTarget.setAttribute('tabindex', '-1');
        }
        focusTarget.focus({ preventScroll: false });
      }
    };

    if (els.switchBtn) {
      els.switchBtn.addEventListener('click', activateOn, { signal: controller.signal });
    }

    if (els.submit) {
      const form = root.querySelector('.bn-pl__form');
      if (form) {
        form.addEventListener('submit', () => { els.submit.disabled = true; }, { signal: controller.signal });
      }
    }

    document.addEventListener('shopify:section:unload', (event) => {
      if (event.target && event.target.contains(root)) {
        stopTimer();
        controller.abort();
        delete root.dataset.bnInit;
      }
    }, { signal: controller.signal });

    render();
    timer = setInterval(tick, 1000);
  };

  const boot = () => {
    document.querySelectorAll('[data-bn-prelaunch]').forEach(init);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', boot);
})();
